import { z } from 'zod'

// Schema para endereços
const enderecoSchema = z.object({
  cep: z.string().optional(),
  logradouro: z.string().min(1, 'Logradouro é obrigatório'),
  numero: z.string().optional(),
  bairro: z.string().optional(),
  cidade: z.string().optional(),
  uf: z.string().optional(),
  complemento: z.string().optional(),
  principal: z.boolean().default(false)
})

// Schema para telefones
const telefoneSchema = z.object({
  telefone: z.string().min(1, 'Telefone é obrigatório'),
  tipo: z.enum(['CELULAR', 'FIXO', 'WHATSAPP', 'OUTRO']).default('CELULAR'),
  principal: z.boolean().default(false)
})

// Schema para emails (permite acentuação)
const emailSchema = z.object({
  email: z
    .string()
    .refine((val) => {
      // Validação de email que permite caracteres acentuados e Unicode
      // Segue RFC 5322 de forma mais flexível, permitindo caracteres internacionais
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(val)
    }, 'Email inválido')
    .transform((val) => (val ? val.toLowerCase() : '')),
  principal: z.boolean().default(false)
})

// Schema para dados do médico
const medicoSchema = z.object({
  crm: z.string().optional(),
  uf_crm: z
    .string()
    .max(2, 'UF deve ter 2 caracteres')
    .optional(),
  id_especialidade: z.string().min(1, 'Especialidade é obrigatória'),
  subespecialidades: z.string().optional(),
  status_crm: z.enum(['ATIVO', 'INATIVO', 'SUSPENSO']),
  data_validade_crm: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true; // Opcional, então vazio é válido

        // Verifica se está no formato dd/MM/yyyy
        const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
        if (!dateRegex.test(val)) {
          return false;
        }

        // Extrai dia, mês e ano
        const partes = val.split('/');
        const dia = parseInt(partes[0]);
        const mes = parseInt(partes[1]);
        const ano = parseInt(partes[2]);

        // Verifica se o ano tem 4 dígitos válidos
        if (ano < 1 || ano > 9999) {
          return false;
        }

        // Verifica se o mês é válido
        if (mes < 1 || mes > 12) {
          return false;
        }

        // Verifica se o dia é válido
        if (dia < 1 || dia > 31) {
          return false;
        }

        // Cria a data de forma explícita para evitar problemas de timezone
        const date = new Date(ano, mes - 1, dia);

        // Verifica se é uma data válida (ex: 31/02 não é válido)
        if (
          date.getFullYear() !== ano ||
          date.getMonth() !== mes - 1 ||
          date.getDate() !== dia
        ) {
          return false;
        }

        return true;
      },
      {
        message: 'Data de validade do CRM inválida. Use o formato dd/MM/yyyy'
      }
    ),
  observacoes: z.string().optional(),
  ativo: z.boolean().default(true)
})

// Schema base para usuário (sem refine para permitir extend)
const usuarioSchemaBase = z.object({
  // Dados da pessoa
  nome: z
    .string()
    .min(1, 'Nome é obrigatório')
    .max(150, 'Nome deve ter no máximo 150 caracteres'),
  tipo: z.enum(['FISICA', 'JURIDICA']).default('FISICA'),
  categoria: z
    .enum(['PACIENTE', 'MEDICO', 'FUNCIONARIO', 'ADMIN', 'EMPRESA', 'OUTRO'])
    .default('FUNCIONARIO'),

  // Dados da pessoa física
  cpf: z.string().optional(),
  cnpj: z.string().optional(),
  nomeFantasia: z.string().optional(),
  inscricaoEstadual: z.string().optional(),
  rg: z.string().max(20, 'RG deve ter no máximo 20 caracteres').optional(),
  dataNascimento: z
    .string()
    .optional()
    .refine(
      val => {
        if (!val || val.trim() === '') return true // Opcional, então vazio é válido

        let dataISO = val

        // Se está no formato YYYY-MM-DD (já convertido), usa diretamente
        const dateRegexISO = /^\d{4}-\d{2}-\d{2}$/
        if (!dateRegexISO.test(val)) {
          // Se está no formato dd/MM/yyyy, converte para ISO
          const dataLimpa = val.replace(/\D/g, '')

          // Verifica se tem 8 dígitos (ddMMyyyy)
          if (dataLimpa.length !== 8) {
            return false
          }

          const dia = dataLimpa.slice(0, 2)
          const mes = dataLimpa.slice(2, 4)
          const ano = dataLimpa.slice(4, 8)

          dataISO = `${ano}-${mes}-${dia}`
        }

        // Valida a data no formato ISO
        const partes = dataISO.split('-')
        const ano = parseInt(partes[0])
        const mes = parseInt(partes[1])
        const dia = parseInt(partes[2])

        // Verifica se o ano tem 4 dígitos válidos
        if (partes[0].length !== 4 || ano < 1 || ano > 9999) {
          return false
        }

        // Verifica se o mês é válido
        if (mes < 1 || mes > 12) {
          return false
        }

        // Verifica se o dia é válido
        if (dia < 1 || dia > 31) {
          return false
        }

        // Cria a data de forma explícita para evitar problemas de timezone
        const date = new Date(ano, mes - 1, dia)

        // Verifica se é uma data válida (ex: 31/02 não é válido)
        if (
          date.getFullYear() !== ano ||
          date.getMonth() !== mes - 1 ||
          date.getDate() !== dia
        ) {
          return false
        }

        return true
      },
      {
        message: 'Data de nascimento inválida. Use o formato dd/MM/yyyy'
      }
    ),
  genero: z.enum(['MASCULINO', 'FEMININO', 'OUTRO']).optional(),

  // Dados do usuário
  email: z
    .string()
    .min(1, 'Email é obrigatório')
    .refine((val) => {
      // Validação de email que permite caracteres acentuados e Unicode
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(val)
    }, 'Email inválido')
    .transform((val) => (val ? val.toLowerCase() : '')),
  username: z
    .string()
    .min(3, 'Username é obrigatório e deve ter no mínimo 3 caracteres')
    .max(30, 'Username deve ter no máximo 30 caracteres')
    .regex(
      /^[a-zA-Z0-9_.]+$/,
      'Username inválido. Use apenas letras, números, ponto e underscore'
    ),
  senha: z
    .string()
    .optional()
    .refine(val => {
      // Se não fornecido ou vazio, é válido (opcional)
      if (!val || val.trim() === '') return true
      // Se fornecido, deve ter no mínimo 6 caracteres
      return val.length >= 6
    }, 'Senha deve ter no mínimo 6 caracteres'),
  id_perfil_acesso: z.string().min(1, 'Perfil de acesso é obrigatório'),
  id_tipo_usuario: z.string().min(1, 'Tipo de usuário é obrigatório'),
  id_empresa: z.string().optional(),
  ativo: z.boolean().default(true),

  // Endereços
  enderecos: z.array(enderecoSchema).optional().default([]),
  emails: z.array(emailSchema).optional().default([]),
  telefones: z.array(telefoneSchema).optional().default([]),

  // Dados do médico (opcional, apenas se for médico)
  medico: medicoSchema.optional()
})

// Schema principal para criar usuário (com refine)
export const criarUsuarioSchema = usuarioSchemaBase
  .refine(
    data => {
      // Se for pessoa física, CPF é obrigatório e válido
      if (data.tipo === 'FISICA') {
        if (!data.cpf || data.cpf.length === 0) {
          return false
        }
        const cpfLimpo = data.cpf.replace(/\D/g, '')
        if (cpfLimpo.length !== 11) {
          return false
        }
      }
      // Se for pessoa jurídica, CNPJ é obrigatório e válido
      if (data.tipo === 'JURIDICA') {
        if (!data.cnpj || data.cnpj.length === 0) {
          return false
        }
        const cnpjLimpo = data.cnpj.replace(/\D/g, '')
        if (cnpjLimpo.length !== 14) {
          return false
        }
      }
      return true
    },
    {
      message:
        'CPF é obrigatório para pessoa física e CNPJ é obrigatório para pessoa jurídica',
      path: ['cpf']
    }
  )
  .refine(
    data => {
      if (data.tipo === 'JURIDICA') {
        if (!data.cnpj || data.cnpj.replace(/\D/g, '').length !== 14) {
          return false
        }
      }
      return true
    },
    {
      message: 'CNPJ é obrigatório e deve ter 14 dígitos para pessoa jurídica',
      path: ['cnpj']
    }
  )

// Schema para atualizar usuário (senha e username opcionais)
export const atualizarUsuarioSchema = usuarioSchemaBase
  .extend({
    senha: z
      .string()
      .optional()
      .refine(val => {
        // Se não fornecido ou vazio, é válido (opcional na edição)
        if (!val || val.trim() === '') return true
        // Se fornecido, deve ter no mínimo 6 caracteres
        return val.length >= 6
      }, 'Senha deve ter no mínimo 6 caracteres')
  })
  .partial()
  .refine(entrada => Object.keys(entrada).length > 0, {
    message: 'Informe ao menos um campo para atualização'
  })

export type CriarUsuarioFormData = z.infer<typeof criarUsuarioSchema>
export type AtualizarUsuarioFormData = z.infer<typeof atualizarUsuarioSchema>
